import { useEffect, useRef } from "react";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import type { AvatarConfig } from "@shared/types";
import { buildRunnerCharacter, animateRunnerIdle } from "../game/engine/runnerCharacter";
import { isLowQuality } from "../game/engine/quality";
import { AvatarPreview } from "./AvatarEditor";

const LOW = isLowQuality();

/**
 * Hero 3D character stage — used by the profile editor (and reusable for the
 * menu). Studio-lit with PMREM environment reflections for the glossy
 * Fall-Guys plastic look, a two-tone podium, and a soft contact shadow.
 *
 * Interaction: drag left/right to spin the character (with momentum). When
 * not being dragged the character just stands there, breathing, with the odd
 * little stretch — it does NOT auto-turntable.
 */
export function AvatarViewer3D({ avatar, interactive = true }: { avatar: AvatarConfig; interactive?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (LOW) return; // low tier: cheap CSS avatar, no WebGL
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const scene = new THREE.Scene();
    scene.background = null;

    // PMREM studio environment → soft reflections that read as plastic.
    let envRT: THREE.WebGLRenderTarget | null = null;
    try {
      const pmrem = new THREE.PMREMGenerator(renderer);
      envRT = pmrem.fromScene(new RoomEnvironment(), 0.04);
      scene.environment = envRT.texture;
      scene.environmentIntensity = 0.7;
      pmrem.dispose();
    } catch { /* env reflections are a nice-to-have */ }

    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 50);
    camera.position.set(0, 3.0, 8.6);
    camera.lookAt(0, 1.85, 0);

    // ── Lighting: warm key + cool fill + pink rim for that toy-studio glow.
    scene.add(new THREE.HemisphereLight(0xfff4e0, 0xb9c7ff, 0.85));
    const key = new THREE.DirectionalLight(0xfff0d0, 2.0);
    key.position.set(-5, 9, 6);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 30;
    key.shadow.camera.left = -5; key.shadow.camera.right = 5;
    key.shadow.camera.top = 6; key.shadow.camera.bottom = -2;
    key.shadow.bias = -0.0006;
    key.shadow.radius = 5;
    scene.add(key);
    const rim = new THREE.PointLight(0xff5fc8, 4.5, 22, 1.6);
    rim.position.set(4.5, 4.0, -4.5);
    scene.add(rim);
    const fill = new THREE.PointLight(0x6fb0ff, 2.4, 24, 1.6);
    fill.position.set(-5, 2.0, 3.5);
    scene.add(fill);

    // ── Two-tone podium (purple base, pink top) like the mockup.
    const podium = new THREE.Group();
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(2.35, 2.7, 0.95, 48),
      new THREE.MeshStandardMaterial({ color: 0x7b54e6, roughness: 0.35, metalness: 0.05, envMapIntensity: 1.1 }),
    );
    base.position.y = 0.48;
    base.receiveShadow = true;
    podium.add(base);
    const lip = new THREE.Mesh(
      new THREE.CylinderGeometry(2.0, 2.0, 0.14, 48),
      new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4 }),
    );
    lip.position.y = 0.99;
    podium.add(lip);
    const top = new THREE.Mesh(
      new THREE.CylinderGeometry(1.85, 1.95, 0.22, 48),
      new THREE.MeshStandardMaterial({ color: 0xff62b0, roughness: 0.32, metalness: 0.04, envMapIntensity: 1.1 }),
    );
    top.position.y = 1.12;
    top.receiveShadow = true;
    podium.add(top);
    scene.add(podium);

    // Soft round contact shadow blob beneath the feet (radial alpha texture).
    const shadowTex = makeRadialAlpha();
    const contact = new THREE.Mesh(
      new THREE.PlaneGeometry(4.2, 4.2),
      new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, opacity: 0.5, depthWrite: false }),
    );
    contact.rotation.x = -Math.PI / 2;
    contact.position.y = 1.235;
    scene.add(contact);

    // Shared character — identical to the in-game player.
    const parts = buildRunnerCharacter(avatar);
    const baseY = 1.23;
    parts.group.position.y = baseY;
    parts.group.traverse((o) => { if (o instanceof THREE.Mesh) { o.castShadow = true; } });
    scene.add(parts.group);

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / Math.max(1, h);
      camera.updateProjectionMatrix();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // ── Drag-to-spin interaction (with momentum).
    let spin = 0.35;          // current yaw
    let spinVel = 0;          // momentum
    let dragging = false;
    let lastX = 0;
    let idleStretchAt = 2 + Math.random() * 3;
    const onDown = (e: PointerEvent) => {
      if (!interactive) return;
      dragging = true; lastX = e.clientX; spinVel = 0;
      canvas.setPointerCapture?.(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      lastX = e.clientX;
      const d = dx * 0.012;
      spin += d;
      spinVel = d;
    };
    const onUp = (e: PointerEvent) => {
      dragging = false;
      try { canvas.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
    };
    if (interactive) {
      canvas.addEventListener("pointerdown", onDown);
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    }

    let raf = 0;
    let last = performance.now();
    const start = last;
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = (now - start) * 0.001;

      // Spin momentum decays when not dragging.
      if (!dragging) {
        spin += spinVel;
        spinVel *= Math.exp(-3.2 * dt);
        if (Math.abs(spinVel) < 0.0002) spinVel = 0;
      }
      parts.group.rotation.y = spin;

      // Idle: gentle breathing + an occasional whole-body stretch.
      animateRunnerIdle(parts, t);
      let stretch = 0;
      if (t > idleStretchAt) {
        const p = (t - idleStretchAt) / 0.9;
        if (p >= 1) { idleStretchAt = t + 3 + Math.random() * 4; }
        else stretch = Math.sin(p * Math.PI);
      }
      parts.group.position.y = baseY + Math.sin(t * 2.1) * 0.03 + stretch * 0.12;
      parts.group.scale.y = 1 + stretch * 0.08;
      parts.group.scale.x = parts.group.scale.z = 1 - stretch * 0.05;

      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      if (interactive) {
        canvas.removeEventListener("pointerdown", onDown);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      }
      shadowTex.dispose();
      envRT?.dispose();
      renderer.dispose();
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          const m = obj.material;
          if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
          else m.dispose();
        }
      });
    };
  }, [avatar.shirtColor, avatar.skinColor, avatar.pantsColor, avatar.hatId, avatar.patternId, interactive]);

  if (LOW) {
    return <div className="avatar-3d-canvas avatar-3d-lite"><AvatarPreview cfg={avatar} large /></div>;
  }
  return <canvas ref={canvasRef} className="avatar-3d-canvas" style={{ touchAction: "none" }} />;
}

/** Soft radial alpha texture for the contact shadow. */
function makeRadialAlpha(): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(64, 64, 4, 64, 64, 62);
  g.addColorStop(0, "rgba(40,20,70,0.85)");
  g.addColorStop(0.6, "rgba(40,20,70,0.35)");
  g.addColorStop(1, "rgba(40,20,70,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
